import React from 'react';
import styled from 'styled-components';

import Backdrop from '@webtv/components/backdrop';
import Portal from '@webtv/components/portal';
import useDialog from '@webtv/hooks/use-dialog';
import useEventListener from '@webtv/hooks/use-event-listener';
import useLockedBody from '@webtv/hooks/use-locked-body';

const dialogSizes = {
  xs: 375,
  sm: 470,
  md: 560,
  lg: 800,
  xl: 900
};

interface DialogContextProps {
  hideCloseIcon?: boolean;
  onClose: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export const DialogContext = React.createContext<DialogContextProps>({} as DialogContextProps);

export const DialogRoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  inset: 0;
  z-index: ${({ theme }) => theme.zIndex.dialog};
  border-radius: 4px;
`;

interface DialogContainerProps {
  maxWidth: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  fullWidth: boolean;
  borderRadius?: string;
}

export const DialogContainer = styled.div<DialogContainerProps>`
  position: relative;
  display: flex;
  max-height: calc(100vh - 64px);
  border-radius: ${(props) => props.borderRadius || '0'};

  background-color: ${(props) => props.theme.palette.background.paper};
  z-index: ${(props) => props.theme.zIndex.dialog};
  max-width: ${(props) => props.maxWidth && `${dialogSizes[props.maxWidth]}px`};
  width: ${(props) => props.fullWidth && props.maxWidth && `${dialogSizes[props.maxWidth]}px`};

  &:focus {
    outline: none;
  }
`;

export interface Props extends React.HTMLAttributes<HTMLDivElement> {
  open: boolean;
  children?: React.ReactNode | React.ReactNode[];
  onClose: () => void;
  hideBackdrop?: boolean;
  disableClickOutside?: boolean;
  disableEscapeKeyDown?: boolean;
  disablePortal?: boolean;
  hideCloseIcon?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  fullWidth?: boolean;
  dialogContainerProps?: any;
  disableLockBodyScroll?: boolean;
}

const DialogInner: React.FC<Props> = (props: Props) => {
  const {
    children,
    onClose,
    disableClickOutside = false,
    disableLockBodyScroll = false,
    disableEscapeKeyDown = false,
    maxWidth = 'md',
    fullWidth = false,
    hideBackdrop = false,
    hideCloseIcon = false,
    dialogContainerProps,
    ...rest
  } = props;

  const dialogRef = React.useRef<HTMLDivElement>(null);

  const handleBackdropClick = React.useCallback(() => {
    if (disableClickOutside || !onClose) return;

    onClose();
  }, [disableClickOutside, onClose]);

  const handleKeyDown = React.useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !disableEscapeKeyDown) {
        event.stopPropagation();

        onClose();
      }
    },
    [disableEscapeKeyDown, onClose]
  );

  React.useEffect(() => {
    dialogRef.current?.focus();
  }, [dialogRef]);

  useLockedBody(!disableLockBodyScroll);

  useEventListener('keydown', (event) => handleKeyDown(event as KeyboardEvent));

  return (
    <DialogContext.Provider value={{ onClose, hideCloseIcon }}>
      <DialogRoot role="presentation" {...rest}>
        {!hideBackdrop && <Backdrop onClick={handleBackdropClick} open={true} />}

        <DialogContainer
          tabIndex={0}
          role="dialog"
          ref={dialogRef}
          aria-modal={true}
          maxWidth={maxWidth}
          fullWidth={fullWidth}
          data-testid={dialogContainerProps?.['data-testid']}
          borderRadius={dialogContainerProps?.borderRadius}
        >
          {children}
        </DialogContainer>
      </DialogRoot>
    </DialogContext.Provider>
  );
};

export const Dialog: React.FC<Props> = (props: Props) => {
  const { open, disablePortal = false } = props;

  const { handleOpen, handleClose } = useDialog();

  React.useEffect(() => {
    handleOpen?.();

    return () => {
      handleClose?.();
    };
  }, [open, handleOpen, handleClose]);

  if (!open) return null;

  return (
    <Portal disabled={disablePortal}>
      <DialogInner {...props} />
    </Portal>
  );
};

export default Dialog;
